import type Highcharts from "highcharts";

import type { Options } from "@ui/Highcharts";
import type { PointOptionsObject } from "highcharts";
import formatNumber from "@ui/i18n/formatNumber";
import t from "../../../../i18n";
import { rawColors } from "@ui/Theme/colors";
import { renderToString } from "react-dom/server";
import React, { useCallback, useMemo } from "react";
import validateInterface from "@ui/lib/validateInterface";
import { MEASURE_POINT_METADATA, PointMetadata } from "../../Chart/PointMetadata";
import formatDate from "@ui/i18n/formatDate";
import Text from "@ui/Text";
import { css } from "@linaria/core";
import { colors, spacings } from "@ui/Theme";

function defineCustomAxisName(unit: string | null): string {
  return unit && unit !== "null" ? `<tspan fill="${rawColors.carbon900}">${unit}</tspan>` : "";
}

export const useOptions = ({
  series,
  activePowerUnit,
  minPeriod,
  maxPeriod,
  yAxisId,
  timeZone,
}: {
  series: Highcharts.SeriesLineOptions[];
  activePowerUnit: string;
  minPeriod: number;
  maxPeriod: number;
  yAxisId: string;
  timeZone: string;
}): Options => {
  const maxValue = useMemo(() => {
    const numericData = (series[0]?.data ?? []).flatMap(point => {
      const data = point as PointOptionsObject;
      if (data.y === null || data === undefined || data.y === undefined) return [];
      return [data.y];
    });
    return Math.max(...numericData);
  }, [series]);

  const yAxisName = defineCustomAxisName(activePowerUnit);

  const constantTooltipFormatter = useCallback(
    function (this: Highcharts.TooltipFormatterContextObject) {
      const metadata = validateInterface(PointMetadata, this.point.options.custom);
      const human = metadata.type === MEASURE_POINT_METADATA ? metadata.human : null;
      const value = human?.value ?? this.point.y;

      const quantityFieldDate =
        metadata.type === MEASURE_POINT_METADATA
          ? formatDate(metadata.date, "datetime", timeZone)
          : null;
      const quantityFieldValue =
        value === undefined
          ? "-"
          : formatNumber(value as number, {
              sign: human?.sign ?? undefined,
              precision: human?.precision ?? undefined,
              unit: human?.unit ?? undefined,
            });

      return renderToString(
        <div className={productionMonitoringTooltip}>
          <Text type="body2">{quantityFieldDate}</Text>
          {metadata.active === false && (
            <Text className={activeState} type="body2SemiBold">
              {t("project.widget.production_monitoring.no_active_analysis")}
            </Text>
          )}
          <Text type="body2SemiBold">
            {`${t("project.widget.productio_monitoring.tooltip.value")} ${quantityFieldValue}`}
          </Text>
        </div>,
      );
    },
    [timeZone],
  );

  return useMemo(
    () => ({
      initial: {
        xAxis: {
          min: minPeriod,
          max: maxPeriod,
        },
      },
      chart: {
        type: "line",
        zoomType: undefined,
        marginTop: 15,
        marginRight: 5,
        marginLeft: 50,
      },
      series,
      xAxis: {
        type: "datetime",
        tickPixelInterval: 150,
        labels: {
          style: {
            fontSize: "8px",
            fontWeight: "400",
            pointerEvents: "none",
            lineHeight: "12px",
          },
        },
      },
      yAxis: [
        {
          id: yAxisId,
          visible: true,
          tickAmount: 5,
          tickPixelInterval: 60,
          title: {
            useHTML: false,
            text: yAxisName,
            style: {
              fontSize: "10px",
              fontWeight: "600",
              pointerEvents: "none",
              lineHeight: "12px",
            },
          },
          labels: {
            sign: false,
            enabled: true,
            unit: undefined,
            style: {
              fontSize: "8px",
              fontWeight: "400",
              pointerEvents: "none",
              lineHeight: "12px",
            },
          },
          plotLines: [
            {
              value: maxValue,
              color: rawColors.titanium200,
              width: 2,
              dashStyle: "Dash",
              label: {
                useHTML: true,
                text: t(
                  "project.widget.production_monitoring.yAxis.max_value",
                  formatNumber(maxValue, { precision: 0, unit: activePowerUnit }),
                ),
                align: "left",
                verticalAlign: "bottom",
                y: -12,
                x: -1,
                style: {
                  backgroundColor: rawColors.titanium100,
                  color: rawColors.carbon900,
                  padding: "4px",
                  borderTopLeftRadius: "4px",
                  borderTopRightRadius: "4px",
                  fontWeight: "bold",
                  fontSize: "10px",
                  pointerEvents: "none",
                },
              },
              zIndex: -1,
            },
          ],
        },
      ],
      tooltip: {
        sticky: true,
        formatter: constantTooltipFormatter,
      },
      navigator: {
        enabled: false,
      },
    }),
    [
      activePowerUnit,
      constantTooltipFormatter,
      maxPeriod,
      maxValue,
      minPeriod,
      series,
      yAxisId,
      yAxisName,
    ],
  );
};

//#region CSS
const productionMonitoringTooltip = css`
  color: ${colors.carbon900};
`;

const activeState = css`
  color: ${colors.carbon700};
  margin-bottom: ${spacings.xs};
`;
//#endregion
